import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Category } from "@/types/category";

export const useCategories = () => {
  const queryClient = useQueryClient();

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select(`
          *,
          category_groups (
            id,
            name
          ),
          quote_categories!inner (
            quote_id,
            quotes (
              id
            )
          )
        `)
        .order("name");
        
      if (error) throw error;
      
      return data.map((category: any) => ({
        id: category.id,
        name: category.name,
        description: category.description || null,
        created_at: category.created_at,
        categoryGroupId: category.category_group_id,
        groupName: category.category_groups ? category.category_groups.name : null,
        quoteCount: category.quote_categories?.filter(qc => qc.quotes).length || 0
      }));
    },
    staleTime: 0,
    gcTime: 30 * 60 * 1000,
  });

  const addCategory = useMutation({
    mutationFn: async (data: { name: string; categoryGroupId?: string | null }) => {
      const { data: newCategory, error } = await supabase
        .from("categories")
        .insert([{ 
          name: data.name,
          category_group_id: data.categoryGroupId 
        }])
        .select()
        .single();

      if (error) throw error;
      return newCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({
        title: "Category added",
        description: "The category has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to add category",
      });
    },
  });

  const updateCategory = useMutation({
    mutationFn: async (data: { id: string; name: string; categoryGroupId?: string | null }) => {
      const { data: updatedCategory, error } = await supabase
        .from("categories")
        .update({ 
          name: data.name,
          category_group_id: data.categoryGroupId 
        })
        .eq('id', data.id)
        .select()
        .single();

      if (error) throw error;
      return updatedCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({
        title: "Category updated",
        description: "The category has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update category",
      });
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("categories")
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({
        title: "Category deleted",
        description: "The category has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete category",
      });
    },
  });

  return {
    categories,
    isLoading,
    addCategory,
    updateCategory,
    deleteCategory
  };
}; 